/** @format */

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocale } from "../../i18n/hooks";
import { LocaleText } from "../../i18n/init";
import { Card, DownloadFile, LocaleType } from "../../resources/types";
import { Alert, Button } from "react-bootstrap";

import { produce } from "immer";
import DownloadItem from "./DownloadItem";
import "./addDownload.scss";

export type Download = {
  fileName?: Partial<LocaleText> | string;
  url: Partial<LocaleText> | string;
};

type Props = {
  updateCard: (value: Card) => void;
  validated?: boolean;
  card: Card;
};

const AddDownload: React.FC<Props> = ({ updateCard, validated, card }) => {
  const { t: _ } = useTranslation();
  const locale = useLocale();

  const [downloadToDeleteId, setDownloadToDeleteId] = useState<number>();

  useEffect(() => {
    if (
      card.Object.downloadFiles &&
      typeof card.Object.downloadFiles !== "object"
    ) {
      updateCard(
        produce(card, (draft) => {
          let _downloadFiles = {
            en: [],
            cy: [],
          };

          draft.Object.downloadFiles = {
            ..._downloadFiles,
            [locale]: card.Object.downloadFiles,
          };
        }),
      );
    }
  }, [card, locale, updateCard]);

  const downloads =
    (Array.isArray(card?.Object.downloadFiles)
      ? card?.Object.downloadFiles
      : card?.Object.downloadFiles?.[locale]) ?? [];

  let downloadToDeleteFilename;
  let downloadToDeleteUrl;

  if (downloadToDeleteId !== undefined) {
    let downloadToDelete = downloads[downloadToDeleteId];
    if (!!downloadToDelete) {
      downloadToDeleteFilename =
        typeof downloadToDelete.fileName === "object"
          ? downloadToDelete?.fileName?.[locale]
          : downloadToDelete?.fileName;
      downloadToDeleteUrl =
        typeof downloadToDelete.url === "object"
          ? downloadToDelete?.url?.[locale]
          : downloadToDelete?.url;
    }
  }

  const setDownload = (
    index: number,
    type: "fileName" | "url",
    value: string,
  ) => {
    updateCard(
      produce(card, (draft) => {
        const downloads = (
          draft.Object.downloadFiles as LocaleType<Array<DownloadFile>>
        )?.[locale];
        downloads[index][type] = value;
      }),
    );
  };

  // TODO: Implement moveDownload function
  /* const moveDownload = (index: number, direction: "up" | "down") => {
    updateCard(
      produce(card, (draft) => {
        const downloads = (
          draft.Object.downloadFiles as LocaleType<Array<DownloadFile>>
        )?.[locale];

        if (direction === "up" && index > 0) {
          const temp = downloads[index];
          downloads[index] = downloads[index - 1];
          downloads[index - 1] = temp;
        } else if (direction === "down" && index < downloads.length - 1) {
          const temp = downloads[index];
          downloads[index] = downloads[index + 1];
          downloads[index + 1] = temp;
        }
      })
    )
  } */

  const handleAddDownload = () => {
    updateCard(
      produce(card, (draft) => {
        if (
          !draft.Object.downloadFiles ||
          Array.isArray(draft.Object.downloadFiles)
        ) {
          let _downloadFiles = { en: [], cy: [] };

          draft.Object.downloadFiles = {
            ..._downloadFiles,
            [locale]: draft.Object.downloadFiles ?? [],
          };
        }

        draft.Object.downloadFiles?.[locale].push({
          fileName: "",
          url: "",
        });
      }),
    );
  };

  const handleRemoveDownload = (index: number) => {
    updateCard(
      produce(card, (draft: Card) => {
        if (
          draft.Object.downloadFiles &&
          Array.isArray(draft.Object.downloadFiles)
        ) {
          let _downloadFiles = { en: [], cy: [] };

          draft.Object.downloadFiles = {
            ..._downloadFiles,
            [locale]: draft.Object.downloadFiles,
          };
        }

        if (draft.Object.downloadFiles)
          draft.Object.downloadFiles[locale].splice(index, 1);

        return draft;
      }),
    );
  };

  return (
    <>
      <div className="download-container">
        <p>
          <strong>
            {_("Provide a link below and click the '+' to add a download.")}
          </strong>
        </p>

        <div className="downloads-list">
          {downloads.map((download: Download, index: number) => (
            <DownloadItem
              key={index}
              download={download}
              index={index}
              setDownload={setDownload}
              setDownloadToDeleteId={setDownloadToDeleteId}
              validated={validated}
            />
          ))}
        </div>
        <Button
          variant="outline-editing"
          className="fs-4 py-0 mt-3 w-100"
          onClick={() => handleAddDownload()}
        >
          +
        </Button>
      </div>
      {downloadToDeleteId !== undefined && (
        <Alert
          variant="danger"
          className="d-flex flex-column flex-sm-row mt-3"
          aria-label="delete-download-alert"
        >
          <div className="flex-grow-1">
            <Alert.Heading>
              {_("Are you sure you want to delete this download?")}
            </Alert.Heading>
            <p className="text-break">
              {!!downloadToDeleteFilename ? (
                <>
                  {downloadToDeleteFilename} <br />
                </>
              ) : (
                <>
                  {downloadToDeleteUrl?.split("/").pop()}
                  <br />
                </>
              )}
            </p>
            <p>
              <strong>{_("This action cannot be undone.")}</strong>
            </p>
          </div>
          <div className="d-inline-block mt-auto flex-shrink-0 justify-content-end">
            <Button
              variant="outline-danger"
              className="cancel me-2"
              onClick={() => {
                setDownloadToDeleteId(undefined);
              }}
            >
              {_("Cancel")}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                handleRemoveDownload(downloadToDeleteId);
                setDownloadToDeleteId(undefined);
              }}
            >
              {_("Delete")}
            </Button>
          </div>
        </Alert>
      )}
    </>
  );
};

export default AddDownload;
